import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Language as LanguageIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import LegalDocumentModal from '../Legal/LegalDocumentModal';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import useThemeStyles from '../../theme/useThemeStyles';

function Settings() {
  const styles = useThemeStyles();
  const { _user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [settings, setSettings] = useState({
    notifications: {
      email: true,
      push: false,
      sms: false,
      marketing: false
    },
    privacy: {
      showProfile: true,
      shareData: false
    },
    preferences: {
      darkMode: false,
      language: 'en'
    }
  });
  const [legalDocuments, setLegalDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [legalModalOpen, setLegalModalOpen] = useState(false);

  useEffect(() => {
    fetchSettings();
    loadLegalDocuments();
  }, []);

  const loadLegalDocuments = async () => {
    try {
      const response = await api.get('/api/legal', { params: { userType: 'user' } });
      setLegalDocuments(response.data.documents || []);
    } catch (err) {}
  };

  const handleLegalDocClick = (doc) => {
    setSelectedDocument(doc);
    setLegalModalOpen(true);
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/users/settings');
      if (response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (category, setting) => (event) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [setting]: event.target.checked
      }
    }));
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      await api.put('/api/users/settings', settings);
      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    try {
      setSaving(true);
      await api.put('user/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      setSuccess('Password changed successfully!');
      setPasswordDialogOpen(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      setError('Please type DELETE to confirm');
      return;
    }

    try {
      await api.delete('user/account');
      logout();
      navigate('/');
    } catch (error) {
      setError('Failed to delete account');
    }
  };

  if (loading) {
    return (
      <Box sx={styles.patterns.loadingState}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.container.page}>
      <Typography component="h2" className="pagename" sx={styles.text.pagename}>
        Settings
      </Typography>
      <Typography variant="body1" sx={styles.text.sectionDescription}>
        Manage your account preferences and privacy settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Notifications */}
        <Grid item xs={12} md={6}>
          <Card sx={styles.card.default}>
            <CardContent sx={styles.spacing.card}>
              <Box sx={{ ...styles.layout.flexRow, mb: 2, gap: 1 }}>
                <NotificationsIcon color="primary" />
                <Typography variant="h6" sx={styles.text.sectionTitle}>
                  Notifications
                </Typography>
              </Box>
              
              <List>
                <ListItem>
                  <ListItemText
                    primary="Email Notifications"
                    secondary="Receive updates via email"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.notifications.email}
                      onChange={handleSettingChange('notifications', 'email')}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <ListItem>
                  <ListItemText
                    primary="Push Notifications"
                    secondary="Receive browser notifications"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.notifications.push}
                      onChange={handleSettingChange('notifications', 'push')}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <ListItem>
                  <ListItemText
                    primary="SMS Notifications"
                    secondary="Receive text messages"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.notifications.sms}
                      onChange={handleSettingChange('notifications', 'sms')}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <ListItem>
                  <ListItemText
                    primary="Marketing Emails"
                    secondary="Receive promotional content"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.notifications.marketing}
                      onChange={handleSettingChange('notifications', 'marketing')}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Privacy */}
        <Grid item xs={12} md={6}>
          <Card sx={styles.card.default}>
            <CardContent sx={styles.spacing.card}>
              <Box sx={{ ...styles.layout.flexRow, mb: 2, gap: 1 }}>
                <SecurityIcon color="primary" />
                <Typography variant="h6" sx={styles.text.sectionTitle}>
                  Privacy & Security
                </Typography>
              </Box>
              
              <List>
                <ListItem>
                  <ListItemText
                    primary="Public Profile"
                    secondary="Allow others to see your profile"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.privacy.showProfile}
                      onChange={handleSettingChange('privacy', 'showProfile')}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <ListItem>
                  <ListItemText
                    primary="Data Sharing"
                    secondary="Share data with partners"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.privacy.shareData}
                      onChange={handleSettingChange('privacy', 'shareData')}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <Divider sx={{ my: 2 }} />
                
                <ListItem button onClick={() => setPasswordDialogOpen(true)}>
                  <ListItemText
                    primary="Change Password"
                    secondary="Update your account password"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Legal Documents */}
        <Grid item xs={12} md={6}>
          <Card sx={styles.card.default}>
            <CardContent sx={styles.spacing.card}>
              <Box sx={{ ...styles.layout.flexRow, mb: 2, gap: 1 }}>
                <DescriptionIcon color="primary" />
                <Typography variant="h6" sx={styles.text.sectionTitle}>
                  Legal Documents
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                View all terms, policies, and legal documents
              </Typography>
              
              <List>
                {legalDocuments.map((doc) => (
                  <ListItem 
                    key={doc.fileName}
                    button 
                    onClick={() => handleLegalDocClick(doc)}
                  >
                    <ListItemText
                      primary={doc.title}
                      secondary={doc.buttonText}
                    />
                  </ListItem>
                ))}
                {legalDocuments.length === 0 && (
                  <ListItem>
                    <ListItemText
                      primary="No legal documents available"
                      secondary="Documents will appear here once configured in admin"
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Preferences */}
        <Grid item xs={12} md={6}>
          <Card sx={styles.card.default}>
            <CardContent sx={styles.spacing.card}>
              <Box sx={{ ...styles.layout.flexRow, mb: 2, gap: 1 }}>
                <LanguageIcon color="primary" />
                <Typography variant="h6" sx={styles.text.sectionTitle}>
                  Preferences
                </Typography>
              </Box>
              
              <List>
                <ListItem>
                  <ListItemText
                    primary="Dark Mode"
                    secondary="Use dark theme"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.preferences.darkMode}
                      onChange={handleSettingChange('preferences', 'darkMode')}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Danger Zone */}
        <Grid item xs={12} md={6}>
          <Card sx={{ ...styles.card.default, borderColor: 'error.main', borderWidth: 2 }}>
            <CardContent sx={styles.spacing.card}>
              <Box sx={{ ...styles.layout.flexRow, mb: 2, gap: 1 }}>
                <WarningIcon color="error" />
                <Typography variant="h6" color="error">
                  Danger Zone
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Irreversible actions that affect your account
              </Typography>
              
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                fullWidth
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete Account
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ ...styles.layout.flexCenter, mt: 3 }}>
        <Button
          variant="contained"
          onClick={saveSettings}
          disabled={saving}
          size="large"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Box sx={styles.patterns.formContainer} mt={2}>
            <TextField
              fullWidth
              type="password"
              label="Current Password"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
            />
            <TextField
              fullWidth
              type="password"
              label="New Password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
            />
            <TextField
              fullWidth
              type="password"
              label="Confirm New Password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
          <Button onClick={handlePasswordChange} variant="contained" disabled={saving}>
            {saving ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle color="error">Delete Account</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action cannot be undone. All your data will be permanently deleted.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please type <strong>DELETE</strong> to confirm:
          </Typography>
          <TextField
            fullWidth
            value={deleteConfirmation}
            onChange={(e) => setDeleteConfirmation(e.target.value)}
            placeholder="Type DELETE"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteAccount} 
            variant="contained" 
            color="error"
            disabled={deleteConfirmation !== 'DELETE'}
          >
            Delete My Account
          </Button>
        </DialogActions>
      </Dialog>

      {/* Legal Document Modal */}
      {selectedDocument && (
        <LegalDocumentModal
          open={legalModalOpen}
          onClose={() => {
            setLegalModalOpen(false);
            setSelectedDocument(null);
          }}
          fileName={selectedDocument.fileName}
          title={selectedDocument.title}
        />
      )}
    </Box>
  );
}

export default Settings; 